import { EditorState, type Extension } from "@codemirror/state";
import { EditorView, keymap, ViewPlugin } from "@codemirror/view";
import { defaultKeymap, history, historyKeymap } from "@codemirror/commands";
import { javascript } from "@codemirror/lang-javascript";
import {
  syntaxHighlighting,
  defaultHighlightStyle,
} from "@codemirror/language";
import { yCollab, yUndoManagerKeymap } from "y-codemirror.next";
import type { Text as YText } from "yjs";
import type { WorkerClient } from "@diagram/ts-worker";
import type { FileStore } from "../store/types.js";
import type { CollaborativeTextProvider } from "../store/collaborative-text.js";
import { tsExtensions, isRemote } from "../ts-extensions/index.js";

/**
 * Character-level diff for applyRemoteChange. Finds first and last differing
 * index, returns the range to replace and the new text. Exported for tests.
 */
export function diff(
  oldStr: string,
  newStr: string,
): { from: number; to: number; insert: string } | null {
  if (oldStr === newStr) return null;
  let from = 0;
  while (
    from < oldStr.length &&
    from < newStr.length &&
    oldStr[from] === newStr[from]
  )
    from++;
  let oldEnd = oldStr.length;
  let newEnd = newStr.length;
  while (
    oldEnd > from &&
    newEnd > from &&
    oldStr[oldEnd - 1] === newStr[newEnd - 1]
  ) {
    oldEnd--;
    newEnd--;
  }
  return {
    from,
    to: oldEnd,
    insert: newStr.slice(from, newEnd),
  };
}

/**
 * Manages per-file CodeMirror EditorState instances, tab switching, and
 * collaborative sync. Sits between FileStore and EditorView.
 */
export class EditorStateManager {
  private states = new Map<string, EditorState>();
  /**
   * The shared text each open file is bound to, for files on the CRDT path.
   * Holding the text rather than a flag is what lets the manager notice the
   * host swapping a document underneath an open file — see
   * {@link rebindIfReplaced}.
   */
  private readonly boundText = new Map<string, YText>();
  /**
   * The text each binding started from, so a document swapped underneath an
   * open file can tell "nobody else has written yet" — the case in which the
   * orphan's edits can be carried across without overwriting anyone.
   */
  private readonly boundBaseText = new Map<string, string>();
  private scrollPositions = new Map<string, { left: number; top: number }>();
  private openTabs: string[] = [];
  private activeTab: string | null = null;
  private view: EditorView | null = null;
  private unsubscribers: (() => void)[] = [];

  constructor(
    private readonly fileStore: FileStore,
    private readonly workerClient: WorkerClient,
    private readonly onNavigate: (path: string, offset: number) => void,
    private readonly onTabsChange: (
      tabs: string[],
      activeTab: string | null,
    ) => void,
    private readonly onContentChange?: () => void,
    private readonly onReferences?: (
      references: { path: string; start: number; end: number }[],
    ) => void,
    /** When true, the editor is non-editable and does not write back to the file store. */
    private readonly readOnly?: boolean,
    /**
     * Optional source of shared `Y.Text` documents. When it has a text for a
     * path, that file is edited collaboratively: CodeMirror binds straight to
     * the CRDT and neither the debounced whole-document write-back nor the
     * diff-based remote apply runs for it.
     */
    private readonly collaborativeText?: CollaborativeTextProvider,
  ) {}

  /**
   * The shared text for a path, or undefined when the file is not
   * collaborative and the `FileStore` owns it.
   *
   * Only consulted when a file is *opened*. Everything afterwards asks
   * {@link isCollaborative}, because the provider's answer can change while
   * a file is open — a backend loading asynchronously starts returning a
   * `Y.Text` for a file the editor already bound to the `FileStore` — and a
   * file that switched channels mid-flight ends up on neither.
   */
  private sharedText(path: string) {
    return this.collaborativeText?.getText(path);
  }

  /**
   * Whether the *open* editor state for a path is bound to a shared CRDT.
   *
   * This is the record of what `buildExtensions` actually decided, not a
   * fresh question to the provider. A file the provider had nothing for at
   * open time is on the `FileStore` path, with a debounced write-back and
   * diff-based remote apply, and it has to keep receiving remote changes
   * through that path or it goes stale and writes its stale buffer back.
   * Closing and reopening the file re-decides.
   */
  private isCollaborative(path: string): boolean {
    return this.boundText.has(path);
  }

  /**
   * Rebuild a file's editor state when the host replaces its shared
   * document.
   *
   * A backend can legitimately swap one: two clients opening a project whose
   * files predate collaborative text both create a document for it, and
   * last-write-wins leaves one of them orphaned. The client holding the
   * orphan would otherwise keep typing into a document nobody else reads —
   * its edits reaching no one, and no one's reaching it — for the rest of
   * the session.
   *
   * Both documents start from the same text, so the rebuild costs a lost undo
   * history; the cursor is carried across, and so is anything typed into the
   * orphan when that can be done without overwriting the winner — see
   * {@link carryOrphanEdits}.
   */
  private rebindIfReplaced(path: string): void {
    const bound = this.boundText.get(path);
    if (!bound) return;
    const current = this.sharedText(path);
    if (!current || current === bound) return;

    this.carryOrphanEdits(path, bound, current);

    const isActive = this.activeTab === path && this.view !== null;
    const cursor = isActive ? this.view?.state.selection.main.head : undefined;
    const content = current.toJSON();

    this.states.delete(path);
    const state = EditorState.create({
      doc: content,
      extensions: this.buildExtensions(path),
      ...(cursor != null && {
        selection: { anchor: Math.min(cursor, content.length) },
      }),
    });
    this.states.set(path, state);
    if (isActive) this.view?.setState(state);
  }

  /**
   * Move whatever was typed into an orphaned document into the winning one.
   *
   * The orphan holds this client's work and nobody else can see it, so
   * rebinding without this drops however much the user typed between the race
   * and its detection — silently, which is the worst way to lose text.
   *
   * It is only safe to carry when the winner still reads exactly as this
   * binding's document did when it started: then the difference between them
   * is entirely this client's, and replaying it overwrites nothing. Once
   * another writer has touched the winner, a textual replay would delete
   * their work to make room for ours, which is the very failure the CRDT
   * exists to prevent — so that case keeps the winner and says loudly what it
   * could not carry, rather than guessing.
   */
  private carryOrphanEdits(path: string, orphan: YText, winner: YText): void {
    const base = this.boundBaseText.get(path);
    const orphaned = orphan.toJSON();
    // Nothing was typed into the orphan, so there is nothing at risk — the
    // winner simply wins, however far ahead of the base it has moved.
    if (base === undefined || orphaned === base) return;

    const winnerText = winner.toJSON();
    const carry = diff(winnerText, orphaned);
    if (!carry) return;

    if (winnerText !== base) {
      console.warn(
        `editor: the shared document for ${path} was replaced after another ` +
          `writer had already changed it; ${String(orphaned.length)} characters ` +
          `written against the replaced document could not be carried over`,
      );
      return;
    }

    if (carry.to > carry.from) winner.delete(carry.from, carry.to - carry.from);
    if (carry.insert.length > 0) winner.insert(carry.from, carry.insert);
  }

  /**
   * The state to show for a file, reconciled against its shared document.
   *
   * A collaborative file that is not the active tab stops tracking its
   * `Y.Text` entirely. `EditorView.setState` destroys every view plugin, and
   * `y-codemirror.next`'s sync plugin unobserves the text in `destroy` — its
   * constructor re-observes but never reconciles what it finds. So the state
   * parked in `states` freezes at the text it had when the user switched
   * away, while every other writer's edit continues to land in the document.
   * Nothing else covers this: `applyRemoteChange` is deliberately inert for
   * collaborative paths, precisely so it cannot fight the CRDT.
   *
   * Switching back therefore has to rebuild from the document. The edit that
   * follows is not corrupted if this is skipped — Yjs maps the offsets
   * through — but the user reads, and edits around, text that no longer
   * exists, and the tab stays wrong until the page is reloaded.
   */
  private reconciledState(path: string): EditorState | undefined {
    const live =
      path === this.activeTab && this.view ? this.view.state : undefined;
    const stored = live ?? this.states.get(path);
    if (!stored) return undefined;
    const shared = this.boundText.get(path);
    if (!shared) return stored;

    const content = shared.toJSON();
    if (stored.doc.toString() === content) return stored;

    const anchor = Math.min(stored.selection.main.head, content.length);
    const state = EditorState.create({
      doc: content,
      extensions: this.buildExtensions(path),
      selection: { anchor },
    });
    this.states.set(path, state);
    return state;
  }

  /**
   * Current content of a file. Prefers the shared CRDT, which is the source
   * of truth when a file has one; the `FileStore` is the fallback and the
   * only source for non-collaborative files.
   *
   * `toJSON()` rather than `toString()`: both return the same string at
   * runtime, but yjs' generated `.d.ts` only declares `toJSON`.
   */
  private contentFor(path: string): string | undefined {
    return this.sharedText(path)?.toJSON() ?? this.fileStore.readFile(path);
  }

  /**
   * Called when the React component mounts the EditorView. Stores the reference.
   */
  attachView(view: EditorView): void {
    this.view = view;
  }

  /**
   * Called on unmount. Clears the view reference.
   */
  detachView(): void {
    this.view = null;
  }

  /**
   * Opens a file in a tab. If already open, switches to it. Otherwise reads
   * from FileStore (or uses the provided fallback content), creates
   * EditorState with TS extensions, and switches.
   */
  openFile(
    path: string,
    fallbackContent?: string,
    cursorOffset?: number,
  ): void {
    if (this.openTabs.includes(path)) {
      this.switchToFile(path);
      if (cursorOffset != null) this.setCursor(cursorOffset);
      return;
    }

    const content = this.contentFor(path) ?? fallbackContent ?? "";
    const extensions = this.buildExtensions(path);
    const selection =
      cursorOffset != null ? { anchor: cursorOffset } : undefined;
    const state = EditorState.create({
      doc: content,
      extensions,
      selection,
    });
    this.states.set(path, state);
    this.openTabs.push(path);
    this.switchToFile(path);
    if (cursorOffset != null) this.setCursor(cursorOffset);
  }

  /**
   * Switches the EditorView to show a different file. Saves and restores scroll.
   * If the tab exists but has no state (e.g. added via handleFileCreated), creates it.
   */
  switchToFile(path: string): void {
    if (!this.states.has(path)) {
      if (this.openTabs.includes(path)) {
        const content = this.contentFor(path) ?? "";
        const state = EditorState.create({
          doc: content,
          extensions: this.buildExtensions(path),
        });
        this.states.set(path, state);
      } else {
        return;
      }
    }

    const state = this.reconciledState(path);
    if (!state) return;

    if (this.view) {
      if (this.activeTab !== null) {
        // Persist the live EditorState so edits survive tab switches
        this.states.set(this.activeTab, this.view.state);
        const scrollLeft = this.view.scrollDOM.scrollLeft;
        const scrollTop = this.view.scrollDOM.scrollTop;
        this.scrollPositions.set(this.activeTab, {
          left: scrollLeft,
          top: scrollTop,
        });
      }

      this.view.setState(state);
      this.view.contentDOM.focus();

      // Restore scroll for target tab
      const saved = this.scrollPositions.get(path);
      if (saved != null) {
        this.view.scrollDOM.scrollLeft = saved.left;
        this.view.scrollDOM.scrollTop = saved.top;
      }
    }

    this.activeTab = path;
    this.onTabsChange([...this.openTabs], this.activeTab);
  }

  /**
   * Moves the cursor to the given offset and scrolls it into view.
   */
  private setCursor(offset: number): void {
    if (!this.view) return;
    this.view.dispatch({
      selection: { anchor: offset },
      effects: EditorView.scrollIntoView(offset, { y: "center" }),
    });
  }

  /**
   * Closes a tab. If it was active, switches to previous or first remaining tab.
   */
  closeFile(path: string): void {
    if (!this.openTabs.includes(path)) return;

    const idx = this.openTabs.indexOf(path);
    this.openTabs.splice(idx, 1);
    this.states.delete(path);
    this.scrollPositions.delete(path);
    // Reopening re-asks the provider, so a file opened before the backend
    // finished loading picks up its shared text on the next open.
    this.boundText.delete(path);
    this.boundBaseText.delete(path);

    if (this.activeTab === path) {
      if (this.openTabs.length === 0) {
        this.activeTab = null;
        if (this.view) {
          this.view.setState(EditorState.create());
        }
      } else {
        const nextPath = idx > 0 ? this.openTabs[idx - 1] : this.openTabs[0];
        this.switchToFile(nextPath);
      }
    }

    this.onTabsChange([...this.openTabs], this.activeTab);
  }

  /** Returns a copy of the open tab paths. */
  getOpenTabs(): string[] {
    return [...this.openTabs];
  }

  /** Returns the path of the currently active tab, or null. */
  getActiveTab(): string | null {
    return this.activeTab;
  }

  /** Returns the current document content for a given path, or null if not open. */
  getDocContent(path: string): string | null {
    const state = this.states.get(path);
    return state ? state.doc.toString() : null;
  }

  /**
   * Handles external file changes. Applies a diff-based transaction with
   * isRemote so the sync extension skips re-syncing.
   *
   * Files bound to a CRDT are exempt: `yCollab` already applies remote
   * operations to the document, and dispatching a second diff-based
   * transaction would be fed straight back into the CRDT as a *local* edit,
   * duplicating it for every other writer.
   *
   * The exemption follows what this editor bound, not what the provider says
   * now. Asking the provider would silence this path for a file that is
   * still on the `FileStore` — leaving it stale on screen while its
   * debounced write-back keeps flushing that stale buffer over everyone
   * else's edits.
   */
  applyRemoteChange(path: string, newContent: string): void {
    if (this.isCollaborative(path)) return;

    if (this.activeTab === path && this.view) {
      // Use the view's live state — it advances on every keystroke/linter update
      // and will reject transactions built from a stale stored state.
      const viewContent = this.view.state.doc.toString();
      const viewDelta = diff(viewContent, newContent);
      if (viewDelta) {
        this.view.dispatch({
          changes: {
            from: viewDelta.from,
            to: viewDelta.to,
            insert: viewDelta.insert,
          },
          annotations: isRemote.of(true),
        });
      }
      return;
    }

    const state = this.states.get(path);
    if (!state) return;

    const oldContent = state.doc.toString();
    const delta = diff(oldContent, newContent);
    if (delta === null) return;

    const tx = state.update({
      changes: { from: delta.from, to: delta.to, insert: delta.insert },
      annotations: isRemote.of(true),
    });
    this.states.set(path, tx.state);
  }

  /**
   * Called when FileStore signals a new file. Adds path to tabs without switching.
   */
  handleFileCreated(path: string): void {
    if (!this.openTabs.includes(path)) {
      this.openTabs.push(path);
      this.onTabsChange([...this.openTabs], this.activeTab);
    }
  }

  /**
   * Called when FileStore signals a file was deleted. Closes tab and syncs delete.
   */

  handleFileDeleted(path: string): void {
    void this.workerClient.deleteFile(path);
    this.closeFile(path);
  }

  /**
   * Subscribes to FileStore events. Returns unsubscribe. Sets up change,
   * created, and deleted handlers.
   */
  subscribe(): () => void {
    const unsubChange = this.fileStore.onFileChange((path, content) => {
      this.rebindIfReplaced(path);
      this.applyRemoteChange(path, content);
      void this.workerClient.syncFile(path, content);
    });
    const unsubCreated = this.fileStore.onFileCreated((path) => {
      this.handleFileCreated(path);
    });
    const unsubDeleted = this.fileStore.onFileDeleted((path) => {
      this.handleFileDeleted(path);
    });
    this.unsubscribers.push(unsubChange, unsubCreated, unsubDeleted);
    return () => {
      unsubChange();
      unsubCreated();
      unsubDeleted();
    };
  }

  private buildExtensions(path: string): Extension[] {
    const shared = this.readOnly ? undefined : this.sharedText(path);
    // Record the decision: every later question about this file answers from
    // here rather than re-asking a provider whose answer may have moved.
    if (shared) {
      this.boundText.set(path, shared);
      this.boundBaseText.set(path, shared.toJSON());
    } else {
      this.boundText.delete(path);
      this.boundBaseText.delete(path);
    }
    const extensions: Extension[] = [
      javascript({ typescript: true }),
      syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
      // A collaborative file gets Yjs' undo manager instead of CodeMirror's:
      // plain history would undo other writers' operations along with your
      // own, because to CodeMirror they are all just document changes.
      //
      // `yCollab` constructs the undo manager but does *not* bind a keymap to
      // it — `yUndoManagerKeymap` is a separate export, and its only other
      // route is a `beforeinput`/`historyUndo` handler that fires solely when
      // the browser happens to have native contenteditable undo state. So
      // without this line Mod-z reaches `historyKeymap`'s `undo`, which is a
      // no-op with no history field, and undo is simply dead on every
      // collaborative file. It goes ahead of the default keymap because
      // earlier extensions win.
      ...(shared ? [keymap.of(yUndoManagerKeymap)] : [history()]),
      keymap.of([...defaultKeymap, ...historyKeymap]),
      ...tsExtensions(
        this.workerClient,
        path,
        (targetPath, targetOffset) => {
          this.onNavigate(targetPath, targetOffset);
        },
        this.onReferences,
      ),
    ];

    if (this.readOnly) {
      // Prevent all user edits in view-only mode
      extensions.push(
        EditorView.editable.of(false),
        EditorState.readOnly.of(true),
      );
    } else if (shared) {
      // The CRDT binding replaces the debounced write-back entirely: every
      // keystroke becomes a Yjs operation that the host persists, so there is
      // no whole-document flush left to clobber a concurrent writer.
      extensions.push(yCollab(shared, null), this.contentChangeExtension());
    } else {
      extensions.push(
        this.fileStoreSyncExtension(path),
        this.contentChangeExtension(),
      );
    }

    return extensions;
  }

  /**
   * Extension that notifies the parent when document content changes locally.
   * Remote changes (from Jazz sync) are excluded to avoid feedback loops.
   *
   * Collaborative files are the exception: `yCollab` applies another writer's
   * operations without the `isRemote` annotation, so they do notify. That is
   * deliberate — the consumer debounces a preview rebuild off this, and a
   * collaborator's edit should refresh the preview like your own does.
   */
  private contentChangeExtension(): Extension {
    const onContentChange = this.onContentChange;
    if (!onContentChange) return [];
    return ViewPlugin.fromClass(
      class {
        update(update: import("@codemirror/view").ViewUpdate) {
          if (!update.docChanged) return;
          if (
            update.transactions.some((tr) => tr.annotation(isRemote) === true)
          )
            return;
          onContentChange();
        }
      },
    );
  }

  /**
   * Debounced write-back to the FileStore so edits persist across reloads.
   * Skips remote changes to avoid write-back loops.
   *
   * Only used for files with no shared `Y.Text`. This is the whole-document
   * write path: it flushes the editor's entire buffer, so anything another
   * writer changed since the buffer was last refreshed is overwritten.
   * Collaborative files use `yCollab` instead.
   */
  private fileStoreSyncExtension(path: string): Extension {
    const fileStore = this.fileStore;
    return ViewPlugin.fromClass(
      class {
        private timeoutId: ReturnType<typeof setTimeout> | null = null;
        constructor(public view: EditorView) {}

        update(update: import("@codemirror/view").ViewUpdate) {
          if (!update.docChanged) return;
          if (
            update.transactions.some((tr) => tr.annotation(isRemote) === true)
          )
            return;

          if (this.timeoutId != null) clearTimeout(this.timeoutId);
          this.timeoutId = setTimeout(() => {
            this.timeoutId = null;
            fileStore.writeFile(path, this.view.state.doc.toString());
          }, 300);
        }

        destroy() {
          if (this.timeoutId != null) clearTimeout(this.timeoutId);
        }
      },
    );
  }

  /** Cleans up all subscriptions and clears state maps. */
  dispose(): void {
    for (const unsub of this.unsubscribers) unsub();
    this.unsubscribers = [];
    this.states.clear();
    this.scrollPositions.clear();
    this.boundText.clear();
    this.boundBaseText.clear();
    this.openTabs = [];
    this.activeTab = null;
  }
}
